import React, { useMemo, useCallback, memo, useContext } from 'react';
import { View, ScrollView, RefreshControl } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { selectAccountLists } from '../../selectors';
import { getListsApi, getListsInProgress } from '../../actions/getLists';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { MaterialCommunityIcons as MCIcons, MaterialIcons as MIcons } from '@expo/vector-icons';
import BookListHor from '../../components/BookListHor';
import BookListHorHeader from '../../components/BookListHorHeader';
import IconPossessed from '../../components/IconPossessed';
import { SECONDARY_COLOR, DEFAULT_LISTS } from '../../constants';
import { useIsLoading, useGetDataOnNavFocus, useListBooks } from '../../hooks';
import { IcoMoon } from '../../utils/customIcons';
import { sortListsAlphabetically, isLargeScreen, getListName } from '../../utils';
import AppSettingsContext from '../../context/AppSettingsContext';

const tBase = 'screens.lists';

const DEFAULT_LISTS_ICONS = {
    wish: <MCIcons name="star" color={SECONDARY_COLOR} size={ 23 } style={{ marginLeft: -1, marginTop: -2 }} />,
    borrowed: <IcoMoon name="download" color={SECONDARY_COLOR} />,
    lent: <IcoMoon name="upload" color={SECONDARY_COLOR} />,
    reading: <MCIcons name='bookmark' color={SECONDARY_COLOR} size={ 22 } style={{ marginLeft: -2, marginTop: -1 }} />,
    unread: <MCIcons name='bookmark-minus' color={SECONDARY_COLOR} size={ 25 } style={{ marginLeft: -3, marginTop: -2 }} />,
    read: <MCIcons name='bookmark-check' color={SECONDARY_COLOR} size={ 25 } style={{ marginLeft: -3, marginTop: -2 }} />,
    toRead: <MIcons name='watch-later' color={SECONDARY_COLOR} size={ 22 } style={{ marginLeft: -1, marginTop: -1 }}/>,
    possessed: <IconPossessed size={ 18 } iconSize={13} bgColor={SECONDARY_COLOR} style={{ marginLeft: 1, marginTop: 1 }}/>
}

export default function ListsScreen({ navigation }) {
    const dispatch = useDispatch();
    const lists = useSelector(selectAccountLists);
    const insets = useSafeAreaInsets();
    const sortedLists = useMemo(() => sortListsAlphabetically(lists), [lists]);
    const isGettingLists = useIsLoading(getListsInProgress);
    const getLists = (checkCaching=true) => {
        !isGettingLists && dispatch(getListsApi({ showLoading: false, checkCaching }));
    }
    useGetDataOnNavFocus(navigation, getLists);
    const paddingBottom = (() => {
        if(isLargeScreen()) return insets.bottom + 50;
        return 50;
    })()
    return (
        <ScrollView 
            contentContainerStyle={{ paddingTop: 10, paddingBottom }}
            refreshControl={
                <RefreshControl
                    refreshing={isGettingLists}
                    onRefresh={() => getLists(false)}
                /> 
            }>
            <Lists lists={sortedLists} navigation={navigation} />
        </ScrollView>
    )
}

const Lists = memo(({ lists, navigation }) => {
    if(!lists) return <View />
    return (
        lists.map(list => (
            <List key={list.id} list={list} navigation={navigation} />
        ))
    )
})

export const List = ({ list, navigation, context, contextId }) => {
    const {t} = useContext(AppSettingsContext);
    const books = useListBooks(list.sort, list.bookIds);
    function getListIcon(list) {
        if(DEFAULT_LISTS.includes(list.type)) {
            return DEFAULT_LISTS_ICONS[list.type];
        }
        return null
    }
    const navigateToList = useCallback(() => navigation.push('List', { listId: list.id }));
    return (
        <View style={{ marginTop: 10 }}>
            <BookListHorHeader
                tBase={tBase} 
                Icon={getListIcon(list)}
                title={getListName(list, t)} 
                isPrivate={list.visibility == 'private'}
                count={list.bookIds.length} 
                onHeaderPress={navigateToList} />
            <BookListHor 
                listId={list.id}
                context={context}
                contextId={contextId}
                books={books} 
                onSeeAllPress={navigateToList}
                navigation={navigation} />
        </View>
    )
}